import { DateInput, toDate } from '@macs-frontend/core/utils/date-utils';
import * as fns from 'date-fns/fp';
import { function as F } from 'fp-ts';
import { StatisticsTime } from '../models/action-team.models';

export function toTimeWithoutZeros(hours: number): string {
  const result = [];
  const intHours = Math.floor(hours);
  const minutes = Math.round(60 * (hours - intHours));
  result.push(intHours);
  if (minutes) {
    result.push(('0' + minutes).slice(-2));
  }
  return result.join(':');
}

export function toTime(minutes: number): string {
  const result = [];
  const hours = Math.floor(minutes / 60);
  const intMinutes = Math.round(minutes % 60);
  result.push(('0' + hours).slice(-2));
  result.push(('0' + intMinutes).slice(-2));

  return result.join(':');
}

/**
 * converts seconds to time HH:mm without days.
 *
 * @example toTimeWithoutDays(86460); // '00:01'
 */
export function toTimeWithoutDays(seconds: number): string {
  if (seconds < 0) {
    return '';
  }
  const result = [];
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  result.push(('0' + (hours % 24)).slice(-2));
  result.push(('0' + (minutes % 60)).slice(-2));

  return result.join(':');
}
export function toHours(timeString: string): number {
  if (!timeString) {
    return 0;
  }
  const parts = timeString.split(':');
  return +(parts[0] || 0) + +(parts[1] || 0) / 60;
}

export function sumTimes(
  times: string[],
  showZeroHours = true,
  showZeroMinutes = true,
): string {
  return secondsToTime(
    times.map(timeToSeconds).reduce((acc, x) => acc + x, 0),
    showZeroHours,
    showZeroMinutes,
  );
}

export function isValidTime(timeString: any) {
  if (typeof timeString === 'number') {
    // eslint-disable-next-line no-param-reassign
    timeString = timeString.toString();
  }
  const timeParts = (timeString || '').split(':');

  return (
    timeParts.length === 2 && timeParts.every((part: string | any[]) => part.length === 2)
  );
}

// HH:mm:ss
export function timeToSeconds(time?: string | null): number {
  if (!time) {
    return 0;
  }

  const timeSplit = time.split(':');

  const hours = parseInt(timeSplit[0] || '', 10) || 0;
  const minutes = parseInt(timeSplit[1] || '', 10) || 0;
  const seconds = parseInt(timeSplit[2] || '', 10) || 0;

  return (hours / 60 + minutes) / 60 + seconds;
}

export function numberToTimeUnit(value: number): string {
  return value >= 100 ? value.toString() : (value / 100).toFixed(2).split('.')[1];
}

export function secondsToTime(
  value?: number,
  showZeroHours = true,
  showZeroMinutes = true,
): string {
  if (!value) {
    return showZeroHours ? '00:00:00' : showZeroMinutes ? '00:00' : '00';
  }

  const seconds = value % 60;
  const minutes = ((value - seconds) / 60) % 60;
  const hours = (value - seconds - minutes * 60) / 60 / 60;

  const result = [numberToTimeUnit(seconds)];

  if (minutes !== 0 || showZeroMinutes || showZeroHours) {
    result.unshift(numberToTimeUnit(minutes));
  }

  if (hours !== 0 || showZeroHours) {
    result.unshift(numberToTimeUnit(hours));
  }

  return result.join(':');
}

export function timeToMinutes(time: any): number {
  if (typeof time === 'number') {
    // eslint-disable-next-line no-param-reassign
    time = time.toString();
  }
  const timeParts = (time || '').split(':');
  const timeObj: StatisticsTime = {
    hours: +timeParts[0] || 0,
    minutes: +timeParts[1] || 0,
  };

  return timeObj.hours * 60 + timeObj.minutes;
}

function pluralize(amount: number, label: string): string {
  if (amount !== 1) {
    // eslint-disable-next-line no-param-reassign
    label += 's';
  }
  return `${amount} ${label}`;
}

export function timeToText(time: string) {
  return minutesToText(timeToMinutes(time));
}

export function minutesToText(durationInMinutes: number) {
  const hours = Math.floor(durationInMinutes / 60);
  const minutes = durationInMinutes % 60;
  const days = Math.floor(durationInMinutes / 1440);
  return [
    { label: 'day', amount: days },
    { label: 'hour', amount: hours },
    { label: 'minute', amount: minutes },
  ]
    .filter(diff => diff.amount > 0)
    .map(diff => pluralize(diff.amount, diff.label))
    .join(', ');
}
/**
 * Calculates number of seconds based on timeString given in hh:mm format
 *
 * @param timeString
 */
export function calcNumSeconds(timeString: string): number {
  const [hours, minutes] = timeString.split(':');
  const numHours = +hours;
  const numMinutes = +minutes;
  return numHours * 3600 + numMinutes * 60;
}

export function secondsToHours(seconds: number, includeSeconds = false): string {
  const hours = (Math.floor(seconds / 3600) + '').padStart(2, '0');
  const mins = (Math.abs(Math.floor((seconds % 3600) / 60)) + '').padStart(2, '0');
  // eslint-disable-next-line functional/no-let
  let str = `${hours}:${mins}`;
  if (includeSeconds) {
    str += ':' + String(seconds % 60).padStart(2, '0');
  }
  return str;
}

export function secondsToH24M(seconds: number, includeSeconds = false): string {
  const hours = ((Math.floor(seconds / 3600) % 24) + '').padStart(2, '0');
  const mins = (Math.abs(Math.floor((seconds % 3600) / 60)) + '').padStart(2, '0');
  // eslint-disable-next-line functional/no-let
  let str = `${hours}:${mins}`;
  if (includeSeconds) {
    str += ':' + String(seconds % 60).padStart(2, '0');
  }
  return str;
}

export function daysFromNow(date: DateInput, prefix = 'Due '): string {
  // eslint-disable-next-line functional/no-let
  let diffString = prefix;

  const diffDays = F.pipe(
    date,
    toDate,
    fns.differenceInDays(F.pipe(new Date(), fns.startOfDay)),
  );
  if (diffDays === 0) {
    diffString += 'today';
  } else if (diffDays > 0) {
    diffString += diffDays === 1 ? 'tomorrow' : `in ${diffDays} days`;
  } else {
    diffString += diffDays === -1 ? 'yesterday' : Math.abs(diffDays) + ' days ago';
  }
  return diffString;
}
